<template>
    <div class="create-edit-admin">
        <div class="create-edit-admin__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div class="create-edit-admin__wrapper">
                        <div class="create-edit-admin__title">{{ router.currentRoute.value.params.id === 'create' ? $t('admin.createAdmin') : $t('admin.editAdmin') }}</div>
                        <div class="create-edit-admin__close-wrapper kasanowa-hover" @click="navigateTo('/dashboard')">
                            <KIcon icon="close" :color="black" width="25" height="25" />
                        </div>
                        <KLoader v-show="loadingWarehouse || loadingAdminDetails" />
                        <Form v-show="!loadingWarehouse && !loadingAdminDetails" :validation-schema="schema" @submit="onSubmit" class="create-edit-admin__form-wrapper">
                            <div class="create-edit-admin__input-wrapper">
                                <KTextField
                                    id="input-login"
                                    v-model="adminForm.login"
                                    name="login"
                                    label=""
                                    :placeholder="$t('admin.loginForm')"
                                />
                                <KTextField
                                    id="input-email"
                                    v-model="adminForm.email"
                                    name="email"
                                    label=""
                                    :placeholder="$t('admin.emailForm')"
                                />
                            </div>
                            <div v-if="isAdmin || isAdminColian" class="create-edit-admin__autocomplete-wrapper">
                                <KCheckbox
                                    id="select-is-admin-colian"
                                    v-model="adminForm.isColianAdmin"
                                    :label="$t('admin.isAdminColian')"
                                    hide-details
                                />
                            </div>
                            <div v-if="isPhReg || isAdminColian || isAdmin" class="create-edit-admin__autocomplete-wrapper">
                                <KCheckbox
                                    id="select-is-admin-reg"
                                    v-model="adminForm.isWarehouseAdmin"
                                    :label="$t('admin.isWarehouseAdmin')"
                                    :disabled="isPhReg"
                                    hide-details
                                />
                            </div>
                            <div v-show="adminForm.isWarehouseAdmin" class="create-edit-admin__autocomplete-wrapper">
                                <KAutocomplete
                                    id="select-warehouse-list"
                                    v-model="adminForm.warehouseIds"
                                    name="warehouseList"
                                    :get-items="warehouseListItems"
                                    item-title="name"
                                    item-value="id"
                                    label=""
                                    :placeholder="$t('admin.warehouse')"
                                    variant="outlined"
                                    clearable
                                />
                            </div>
                            <div class="create-edit-admin__btn-wrapper">
                                <KButton
                                    id="submit-btn"
                                    :label="$t('admin.save')"
                                    type="submit"
                                    icon="arrowRight"
                                    :color="black"
                                    width="11"
                                    height="11"
                                    :disabled="loading"
                                    :loading="loading"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from '@vue/runtime-core';
import router from '@/router';
import { Form } from 'vee-validate';
import * as yup from 'yup';
import WarehouseApi from '@/api/v1/WarehouseApi';
import AdminApi from '@/api/v1/AdminApi';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { toast } from 'vue3-toastify';
import { navigateTo, navigateToWithSuccessToast, navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import { ROLE_ID_ADMIN, ROLE_ID_ADMIN_WHOLESALER, ROLE_ID_ADMIN_ROOT } from '@/hooks/UserHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { IWareHouseItems } from '@/models/WarehouseModel';
import type { IAdminForm } from '@/models/AdminModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import KTextField from '@/components/atoms/KTextField.vue';
import KButton from '@/components/atoms/KButton.vue';
import KAutocomplete from '@/components/atoms/KAutocomplete.vue';
import KLoader from '@/components/atoms/KLoader.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KCheckbox from '@/components/atoms/KCheckbox.vue';

const { isAdmin, isPhReg, isAdminColian } = storeToRefs(useUserStore());
const { black } = variables;

const warehouseApi = new WarehouseApi();
const loadingWarehouse = ref<boolean>(false);
const warehouseListItems = ref<IWareHouseItems[]>([]);
const warehousesList = async() => {
    loadingWarehouse.value = true;
    try {
        const result = await warehouseApi.warehouseList(
            1,
            100,
            null,
            null,
            null,
        );
        if (!result.isSuccess) {
            toast.error(t('admin.toast.errorWarehouseList'));
            loadingWarehouse.value = false;
            return;
        }
        warehouseListItems.value = result.payload.data;
    } catch (e) {
        toast.error(t('admin.toast.errorWarehouseList'));
        loadingWarehouse.value = false;
        return;
    }
    loadingWarehouse.value = false;
};
warehousesList();

const adminForm = ref<IAdminForm>({
    login: null,
    email: null,
    warehouseIds: null,
    isWarehouseAdmin: isPhReg.value,
    isColianAdmin: false,
});
const schema:any = yup.object({
    login: yup.string().required(`${t('admin.validation.loginRequired')}`).label('login'),
    email: yup.string().required(`${t('admin.validation.emailRequired')}`).email(`${t('admin.validation.email')}user@example.pl`).label('email'),
});
const loading = ref<boolean>(false);
const adminApi = new AdminApi();
const onSubmit = async() => {
    loading.value = true;
    if (adminForm.value.isWarehouseAdmin && adminForm.value.warehouseIds == null) {
        toast.error(t('admin.toast.selectWarehouse'));
        loading.value = false;
        return;
    }
    if (router.currentRoute.value.params.id === 'create') {
        try {
            const result = await adminApi.createAdmin({
                name: adminForm.value.login,
                email: adminForm.value.email,
                warehouse_ids: adminForm.value.isWarehouseAdmin ? adminForm.value.warehouseIds : null,
                role: adminForm.value.isWarehouseAdmin ? ROLE_ID_ADMIN_WHOLESALER : adminForm.value.isColianAdmin ? ROLE_ID_ADMIN : ROLE_ID_ADMIN_ROOT,
            });
            if (!result.isSuccess) {
                toast.error(t('admin.toast.errorCreateAdmin'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast('/dashboard', t('admin.toast.successCreateAdmin'));
        } catch (e) {
            toast.error(t('admin.toast.errorCreateAdmin'));
            loading.value = false;
            return;
        }
    } else {
        try {
            const result = await adminApi.editAdmin(
                {
                    name: adminForm.value.login,
                    email: adminForm.value.email,
                    warehouse_ids: adminForm.value.isWarehouseAdmin ? [adminForm.value.warehouseIds] : null,
                    role: adminForm.value.isWarehouseAdmin ? ROLE_ID_ADMIN_WHOLESALER : adminForm.value.isColianAdmin ? ROLE_ID_ADMIN : ROLE_ID_ADMIN_ROOT,
                },
                router.currentRoute.value.params.id,
            );
            if (!result.isSuccess) {
                toast.error(t('admin.toast.errorEditAdmin'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast('/dashboard', t('admin.toast.successEditAdmin'));
        } catch (e) {
            toast.error(t('admin.toast.errorEditAdmin'));
            loading.value = false;
            return;
        }
    }
    loading.value = false;
};

const loadingAdminDetails = ref<boolean>(false);
const adminDetails = async() => {
    loadingAdminDetails.value = true;
    try {
        const result = await adminApi.adminDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast('/dashboard', t('admin.toast.errorAdminDetails'))
            loadingAdminDetails.value = false;
            return;
        }
        adminForm.value.login = result.payload.name;
        adminForm.value.email = result.payload.email;
        if (result.payload.role === ROLE_ID_ADMIN_ROOT) {
            adminForm.value.warehouseIds = null;
            adminForm.value.isWarehouseAdmin = false;
            adminForm.value.isColianAdmin = false;
        }
        if (result.payload.role === ROLE_ID_ADMIN_WHOLESALER) {
            adminForm.value.warehouseIds = result.payload.warehouses[0].id;
            adminForm.value.isWarehouseAdmin = true;
            adminForm.value.isColianAdmin = false;
        }
        if (result.payload.role === ROLE_ID_ADMIN) {
            adminForm.value.warehouseIds = null;
            adminForm.value.isWarehouseAdmin = false;
            adminForm.value.isColianAdmin = true;
        }
    } catch (e) {
        navigateToWithErrorToast('/dashboard', t('salesRep.toast.errorAdminDetails'))
        loadingAdminDetails.value = false;
        return;
    }
    loadingAdminDetails.value = false;
};
if (router.currentRoute.value.params.id !== 'create') {
    adminDetails();
}

watch(() => adminForm.value.isWarehouseAdmin, (newValue, oldValue) => {
    if (newValue) {
        adminForm.value.isColianAdmin = false;
    } else {
        adminForm.value.warehouseIds = null;
    }
}, {
    deep: true,
});
watch(() => adminForm.value.isColianAdmin, (newValue, oldValue) => {
    if (newValue) {
        adminForm.value.isWarehouseAdmin = false;
        adminForm.value.warehouseIds = null;
    }
}, {
    deep: true,
});
</script>

<style scoped lang="scss">
.create-edit-admin {
    position: relative;
    width: 100%;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__wrapper {
        position: relative;
        background: rgba($white, 0.15);
        backdrop-filter: blur(30px) saturate(150%);
        -webkit-backdrop-filter: blur(30px) saturate(150%);
        border-radius: 5px;
        border: 1px solid $secondary-400;
        padding: 45px 15px 15px 15px;
        max-width: 1025px;
        margin: 20px auto 0;

        @include media-breakpoint-up(lg) {
            padding: 124px 52px 60px 52px;
            margin: 50px auto 0;
        }
    }

    &__title {
        width: 90%;
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $primary-400;
        text-transform: uppercase;
        font-weight: 900;
        letter-spacing: 0;
        text-align: center;
        padding: 10px;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 9px;
            top: -50px;
        }
    }

    &__form-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__input-wrapper {
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            gap: 16px;
        }

        &:deep(.k-input .v-field__input) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__autocomplete-wrapper {
        &:deep(.k-autocomplete .v-field) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__btn-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            justify-content: flex-end;
        }
    }

    &__close-wrapper {
        position: absolute;
        top: 18px;
        right: 12px;

        @include media-breakpoint-up(sm) {
            top: 12px;
            right: 12px;
        }
    }
}
</style>
